import React from "react";
import { Field } from "redux-form";
import { required } from "utils/validations";
import PaypairTermsofService from "assets/docs/PaypairTermsofService.pdf";
import PaypairPrivacyPolicy from "assets/docs/PaypairPrivacyPolicy.pdf";
import PaypairPatriotActDisclosure from "assets/docs/PaypairPatriotActDisclosure.pdf";
import { CustomCheckbox } from "components/Controls";
import Tooltip from "@mui/material/Tooltip";
import Fade from "@mui/material/Fade";
import ClickAwayListener from "@mui/material/ClickAwayListener";

export const renderAgreementCheckbox = ({ input, meta }) => {
  return <CustomCheckbox meta={meta} {...input} />;
};

const LinkTooltip = ({ title, children }) => {
  const [open, setOpen] = React.useState(false);

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = () => {
    setOpen(true);
  };

  return (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <Tooltip
        sx={{
          tooltip: {
            backgroundColor: "#F8F8F8",
            color: "#000",
            fontSize: "12px",
            fontFamily: "OpenSans-Regular",
            padding: "10px",
            fontWeight: "normal",
          },
        }}
        className="link"
        onClose={handleTooltipClose}
        open={open}
        disableFocusListener
        disableHoverListener
        interactive
        disableTouchListener
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 200 }}
        title={
          title ? (
            <div
              style={{ height: "100px", minHeight: 300, overflowY: "auto" }}
              dangerouslySetInnerHTML={{ __html: title }}
            />
          ) : (
            ""
          )
        }
      >
        <span onClick={handleTooltipOpen}>{children}</span>
      </Tooltip>
    </ClickAwayListener>
  );
};

const ExternalLink = ({ href, children }) => (
  <a href={href} className="link" target="_blank" rel="noopener noreferrer">
    {children}
  </a>
)

const DisclosureFirstBlock = () => {
  return (
    <div className="privacy-policy-text privacy-policy-scrollable">
      By clicking the “See My Purchase Options” button, I am submitting an
      application for financing or leasing (“Application”), and I agree to the{' '}
      <ExternalLink href={PaypairTermsofService}>Terms of Use</ExternalLink>,{' '}
      <ExternalLink href={PaypairPrivacyPolicy}>Privacy Policy</ExternalLink>, and{' '}
      <ExternalLink href={PaypairPatriotActDisclosure}>Patriot Act Disclosure</ExternalLink>; all
      of the information I have provided on this Application is true and correct. I agree that I am furnishing my Application to Tire Agent
      (“Merchant”), and I give Merchant permission to share my personal information, including my Application, with its service provider,{' '}
      <span className="font-bold">Paypair</span>, for further sharing with lenders, financiers, or lease providers who may be able to provide
      financing or lease options to me for goods and services from Merchant or other merchants that implement the{' '}
      <span className="font-bold">Paypair</span> solution, and for marketing products and services to me. I authorize Merchant,{' '}
      <span className="font-bold">Paypair</span>, and other lenders, financiers, or lease providers to obtain information from my credit
      report or personal information from consumer credit agencies to conduct a pre-qualification for all my financing options.
    </div>
  )
}

const DisclosureSecondBlock = ({ aff, progressive, agreements, katapult, zip }) => {
  return (
    <div className={`privacy-policy-text privacy-policy-scrollable ${progressive && 'scrollable-large'}`}>
      {progressive && (
        <>
          If you are not approved for credit, you direct that your application information be provided to Progressive
          Leasing to attempt to prequalify you for a lease-purchase option. You agree to Progressive Leasing’s{' '}
          <ExternalLink href="https://progleasing.com/privacy">Privacy</ExternalLink> and{' '}
          <ExternalLink href="https://progleasing.com/arbitration-clause">Arbitration Provision</ExternalLink>, which
          will govern your transaction. Categories of personal information are collected and information about you from
          consumer reporting agencies is used to determine pre-qualification eligibility.{' '}
        </>
      )}

      Lenders include but are not limited to West Creek Financial, Inc. dba Koalafi on its own or on behalf of The
      Bank of Missouri. By continuing, you also agree to Koalafi’s{' '}
      <ExternalLink href="https://apply.koalafi.com/electronic-communications?support=lto">esign agreement</ExternalLink>
      ,{' '}
      <ExternalLink href="https://koalafi.com/privacy-policy">privacy policy</ExternalLink>, and{' '}
      <ExternalLink href="https://apply.koalafi.com/terms-and-conditions">terms and conditions</ExternalLink>.{' '}

      {aff && agreements && (
        <>
          By continuing you also agree to American First Finance’s{' '}
          <LinkTooltip title={agreements.paymentAuth?.html}>esign agreement</LinkTooltip>,{' '}
          <ExternalLink href={agreements.privacyPolicy?.file}>privacy policy</ExternalLink>, and{' '}
          <LinkTooltip title={agreements.terms?.html}>terms and conditions</LinkTooltip>.{' '}
        </>
      )}

      {katapult && (
        <>
          By checking this box, I confirm I have read, understood, and consented to the{' '}
          <ExternalLink href="https://katapult.com/legal#credit-disclosure">Credit Report Disclosure</ExternalLink>,{' '}
          <ExternalLink href="https://katapult.com/legal#privacy-policy">Privacy Policy</ExternalLink>,{' '}
          <ExternalLink href="https://katapult.com/legal#esign-disclosure">ESIGN disclosure</ExternalLink> and{' '}
          <ExternalLink href="https://katapult.com/legal#terms-of-use">Terms of Use</ExternalLink>, which contain a
          binding arbitration agreement. I agree that Katapult may contact me by text, SMS, and MMS for login info and
          other application and account-related information. I understand that message and data rates may apply and
          that message frequency may vary.{' '}
        </>
      )}

      {zip && (
        <>
          I authorize Zip Co US Inc. ("Zip") to perform a soft credit inquiry, and obtain a credit report, on behalf of
          its Banking Partner, WebBank, to determine if I prequalify for a loan. I further authorize Zip to share the
          outcome of my prequalification with Tire Agent so that they may display this information with me in connection
          with my potential transaction. I acknowledge that I have reviewed and consent to the{' '}
          <ExternalLink href="https://zip.co/us/loan-terms-of-service">Zip Customer Agreement</ExternalLink>,{' '}
          <ExternalLink href="https://zip.co/us/privacy-policy">Zip Privacy Policy</ExternalLink>,{' '}
          <ExternalLink href="https://zip.co/us/esign-consent">Zip-ESign Consent</ExternalLink> and{' '}
          <ExternalLink href="https://zip.co/us/loan-terms-of-service#3.3">Zip Credit Pull Authorization, Refunds & Return Policy</ExternalLink>.
          I understand that pre-qualification does not guarantee approval or constitute a commitment to lend.
        </>
      )}
    </div>
  )
}

export const renderPrivacyPolicy = (token, providers, agreements, merchantName = "Tire Agent") => {
  const affProvider = providers && providers.includes('aff');
  const progressiveProvider = providers && providers.includes('progressive');
  const katapultProvider = providers && providers.includes('katapult2');
  const zipProvider = providers && providers.includes('zip');

  return (
    <div className="privacy-policy">
      <div className="privacy-policy-agreement tcpa-agreement flex flex__align--start margin-left">
        <Field name="agreeTcpa" component={renderAgreementCheckbox}/>
        <div className="privacy-policy-agreement__label">
          By checking this box, I consent by electronic signature to receive
          text messages from Tire Agent at the phone number I have provided
          made by any means or technology, including an automatic telephone
          dialing system or other automated system for the selection and dialing
          of telephone numbers, for marketing, sales or any other purposes. I
          understand that my consent is not a condition of any purchase from
          Tire Agent. Msg frequency varies. Msg & data rates may apply. View{" "}
          <ExternalLink href="https://www.tireagent.com/terms">Terms</ExternalLink>
          {" "}and{" "}
          <ExternalLink href="https://www.tireagent.com/privacy">Privacy</ExternalLink>.
        </div>
      </div>
      <div className="privacy-policy-agreement flex flex__align--center">
        <div className="triangle">▶</div>
        <Field
          name="isAgreementChecked"
          validate={[required]}
          component={renderAgreementCheckbox}
        />
        <div className="privacy-policy-agreement__label">
          By checking this box, you agree to the disclosure below.
        </div>
      </div>

      <DisclosureFirstBlock />
      <DisclosureSecondBlock progressive={progressiveProvider} aff={affProvider} agreements={agreements} katapult={katapultProvider} zip={zipProvider} />
    </div>
  );
};
